import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Card,
  Chip,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tooltip,
  Typography,
  Button,
  Collapse,
  IconButton,
  CircularProgress,
} from '@mui/material';
import {
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  Calendar,
  Download,
  Eye,
  FileText,
  Folder,
  Headphones,
  Inbox,
  IdCard,
  Mail,
  MapPin,
  Paperclip,
  PieChart,
  PhoneCall,
  PhoneIncoming,
  PhoneOutgoing,
  Receipt,
  RefreshCw,
  Scale,
  Send,
  TrendingUp,
  User,
  WalletCards,
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../services/api';
import { useJob } from '../context/JobContext';
import LoaderProgress from '../components/ui/loader-progress';

const sectionCardSx = {
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: '16px',
  overflow: 'hidden',
};

const formatCurrency = (value) =>
  new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const formatDate = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('it-IT');
};

const formatPercent = (value) => `${Number(value || 0).toFixed(2)}%`;
const formatDecimal = (value) =>
  new Intl.NumberFormat('it-IT', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(Number(value || 0));

const formatDateTimeIt = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = String(d.getFullYear()).slice(-2);
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  const seconds = String(d.getSeconds()).padStart(2, '0');
  return `${day}/${month}/${year}, ${hours}:${minutes}:${seconds}`;
};

const translateCloudtalkStatus = (status) => {
  const map = {
    answered: 'Risposta',
    missed: 'Persa',
    busy: 'Occupato',
    failed: 'Fallita',
    no_answer: 'Non Risposta',
  };
  return map[String(status || '')] || status || '-';
};

const translateCloudtalkDirection = (direction) => {
  const map = {
    inbound: 'Entrata',
    outbound: 'Uscita',
  };
  return map[String(direction || '')] || direction || '-';
};

const formatDuration = (seconds) => {
  const s = Number(seconds || 0);
  if (!s) return '0s';
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = Math.floor(s % 60);
  const parts = [];
  if (h > 0) parts.push(`${h}h`);
  if (m > 0) parts.push(`${m}m`);
  if (sec > 0 || parts.length === 0) parts.push(`${sec}s`);
  return parts.join(' ');
};

const getPhoneSearchCandidates = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return [];
  const digits = raw.replace(/[^\d]/g, '');

  const candidates = [raw];
  if (digits) {
    candidates.push(digits);
    if (digits.startsWith('39') && digits.length > 2) {
      candidates.push(digits.slice(2));
      candidates.push(`+${digits}`);
      candidates.push(`+39${digits.slice(2)}`);
    } else {
      candidates.push(`+${digits}`);
      candidates.push(`+39${digits}`);
    }
    if (digits.length >= 7) candidates.push(digits.slice(-7));
    if (digits.length >= 8) candidates.push(digits.slice(-8));
    if (digits.length >= 9) candidates.push(digits.slice(-9));
  }

  return Array.from(new Set(candidates.map((c) => String(c).trim()).filter(Boolean)));
};

const isSentFolderPath = (value) => {
  const s = String(value || '').trim().toLowerCase();
  if (!s) return false;
  return (
    s.includes('sent') ||
    s.includes('posta inviata') ||
    s.includes('inviata') ||
    s.includes('sent items') ||
    s.includes('outbox')
  );
};

const translateFolderPathIt = (value) => {
  const raw = String(value || '').trim();
  const s = raw.toLowerCase();
  if (!s) return '-';
  if (s === 'inbox') return 'Posta in arrivo';
  if (isSentFolderPath(raw)) return 'Posta inviata';
  if (s.includes('draft')) return 'Bozze';
  if (s.includes('spam') || s.includes('junk')) return 'Spam';
  if (s.includes('trash') || s.includes('deleted')) return 'Cestino';
  if (s.includes('archive')) return 'Archivio';
  return raw;
};

const parseBlobErrorMessage = async (blob) => {
  if (!blob) return null;
  try {
    const text = await blob.text();
    if (!text) return null;
    try {
      const parsed = JSON.parse(text);
      const msg = parsed?.message || parsed?.error || null;
      return msg ? String(msg) : null;
    } catch (_) {
      return String(text);
    }
  } catch (_) {
    return null;
  }
};

const formatPhone = (value) => {
  if (!value) return '-';
  const str = String(value);
  return str.replace(/^(\+39)(\d+)/, '$1 $2');
};

const getPrivacyNames = (call) => {
  const direction = call?.direction;
  const agentName = call?.agent_name || null;
  const contactName = call?.contact_name || null;

  if (direction === 'inbound') {
    return { daName: contactName, aName: agentName };
  }
  if (direction === 'outbound') {
    return { daName: agentName, aName: contactName };
  }
  return { daName: null, aName: null };
};

const renderSectionHeader = (title) => (
  <Box sx={{ p: 3, borderBottom: '1px solid rgba(145, 158, 171, 0.12)' }}>
    <Typography variant="h6" sx={{ fontWeight: 700 }}>
      {title}
    </Typography>
  </Box>
);

const getProgressPercent = (incassato, totale) => {
  const t = Number(totale || 0);
  if (!t) return 0;
  const p = (Number(incassato || 0) / t) * 100;
  if (p < 0) return 0;
  if (p > 100) return 100;
  return p;
};

const parseBirthDateFromCodiceFiscale = (cfValue) => {
  const cf = String(cfValue || '').trim().toUpperCase();
  if (!/^[A-Z0-9]{16}$/.test(cf)) return null;
  const yearPart = cf.slice(6, 8);
  const monthChar = cf.slice(8, 9);
  const dayPart = cf.slice(9, 11);
  const monthMap = {
    A: 0,
    B: 1,
    C: 2,
    D: 3,
    E: 4,
    H: 5,
    L: 6,
    M: 7,
    P: 8,
    R: 9,
    S: 10,
    T: 11,
  };
  const month = monthMap[monthChar];
  if (month == null) return null;
  const yearNum = Number(yearPart);
  let dayNum = Number(dayPart);
  if (!Number.isInteger(yearNum) || !Number.isInteger(dayNum)) return null;
  if (dayNum > 40) dayNum -= 40;
  if (dayNum < 1 || dayNum > 31) return null;
  const nowYear = new Date().getFullYear();
  const currentTwoDigit = nowYear % 100;
  const fullYear = yearNum <= currentTwoDigit ? 2000 + yearNum : 1900 + yearNum;
  const d = new Date(fullYear, month, dayNum);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('it-IT');
};

const getDossierStatusLabel = (status) => {
  if (!status) return '-';
  const map = {
    documents_sent: 'Documenti inviati',
    failed: 'Fallito',
    'in negotiation': 'In trattativa',
    processing: 'In lavorazione',
    submitted_quote: 'Preventivo inviato',
    terminated: 'Terminato',
  };
  return map[status] || status;
};

const getContractTypeLabel = (value) => {
  const raw = String(value || '').trim().toLowerCase();
  if (raw === 'contratto') return 'Contratto';
  if (raw === 'upsell') return 'Upsell';
  return value || '-';
};

export default function PasspEsposizioneContabileDetail() {
  const navigate = useNavigate();
  const { subjectKey } = useParams();
  const { addJob } = useJob();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [expandedAnno, setExpandedAnno] = useState(null);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [cloudtalkCalls, setCloudtalkCalls] = useState([]);
  const [cloudtalkLoading, setCloudtalkLoading] = useState(false);
  const [cloudtalkPage, setCloudtalkPage] = useState(0);
  const [cloudtalkRowsPerPage, setCloudtalkRowsPerPage] = useState(10);
  const [cloudtalkTotal, setCloudtalkTotal] = useState(0);
  const [cloudtalkSearchTerm, setCloudtalkSearchTerm] = useState('');
  const cloudtalkLoadedRef = useRef({
    phone: null,
    searchTerm: null,
    page: null,
    rowsPerPage: null,
  });
  const [recordingDialogOpen, setRecordingDialogOpen] = useState(false);
  const [recordingLoading, setRecordingLoading] = useState(false);
  const [recordingError, setRecordingError] = useState(null);
  const [recordingUrl, setRecordingUrl] = useState(null);
  const [recordingCall, setRecordingCall] = useState(null);
  const [privacyDialogOpen, setPrivacyDialogOpen] = useState(false);
  const [privacyPendingCall, setPrivacyPendingCall] = useState(null);
  const [emailMatches, setEmailMatches] = useState([]);
  const [emailTotal, setEmailTotal] = useState(0);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailPage, setEmailPage] = useState(0);
  const [emailRowsPerPage, setEmailRowsPerPage] = useState(10);
  const [emailSearchTerm, setEmailSearchTerm] = useState('');
  const [emailPrivacyDialogOpen, setEmailPrivacyDialogOpen] = useState(false);
  const [emailPrivacyPending, setEmailPrivacyPending] = useState(null);
  const [emailViewDialogOpen, setEmailViewDialogOpen] = useState(false);
  const [emailViewLoading, setEmailViewLoading] = useState(false);
  const [emailViewError, setEmailViewError] = useState(null);
  const [emailViewData, setEmailViewData] = useState(null);
  const [emailViewRow, setEmailViewRow] = useState(null);

  useEffect(() => {
    const fetchDetail = async () => {
      setLoading(true);
      try {
        const res = await api.get(
          `/passp-esposizione-contabile/subject/${encodeURIComponent(String(subjectKey || ''))}`
        );
        setDetail(res.data || null);
      } catch (error) {
        toast.error(error?.response?.data?.message || 'Errore caricamento dettaglio nominativo');
        navigate('/passp-esposizione-contabile');
      } finally {
        setLoading(false);
      }
    };
    fetchDetail();
  }, [subjectKey, navigate]);

  const anagrafica = detail?.dati_anagrafici || {};
  const totali = detail?.totali || {};
  const dettagli = detail?.dettagli || [];
  const contrattiUpsell = detail?.contratti_upsell || [];
  const fatturato = detail?.fatturato || [];
  const telefono = useMemo(() => String(anagrafica?.telefono || '').trim(), [anagrafica?.telefono]);
  const emailValue = useMemo(() => String(anagrafica?.email || '').trim(), [anagrafica?.email]);
  const pecValue = useMemo(() => String(anagrafica?.pec || '').trim(), [anagrafica?.pec]);
  const emailSearchCandidates = useMemo(
    () => Array.from(new Set([emailValue, pecValue].filter(Boolean))),
    [emailValue, pecValue]
  );

  const title = useMemo(() => anagrafica.nome || 'Dettaglio nominativo', [anagrafica.nome]);
  const birthDateFromCf = useMemo(
    () => parseBirthDateFromCodiceFiscale(anagrafica.codice_fiscale),
    [anagrafica.codice_fiscale]
  );
  const birthDateValue = useMemo(
    () => (anagrafica.data_nascita ? formatDate(anagrafica.data_nascita) : birthDateFromCf),
    [anagrafica.data_nascita, birthDateFromCf]
  );

  useEffect(() => {
    setCloudtalkCalls([]);
    setCloudtalkTotal(0);
    setCloudtalkSearchTerm('');
    setCloudtalkPage(0);
    cloudtalkLoadedRef.current = { phone: null, searchTerm: null, page: null, rowsPerPage: null };
  }, [subjectKey]);

  useEffect(() => {
    if (tab !== 6) return;
    setCloudtalkCalls([]);
    setCloudtalkTotal(0);
    setCloudtalkSearchTerm('');
    setCloudtalkPage(0);
    cloudtalkLoadedRef.current = { phone: null, searchTerm: null, page: null, rowsPerPage: null };
  }, [tab, telefono]);

  useEffect(() => {
    setEmailMatches([]);
    setEmailTotal(0);
    setEmailPage(0);
    setEmailRowsPerPage(10);
    setEmailSearchTerm('');
    setEmailPrivacyDialogOpen(false);
    setEmailPrivacyPending(null);
    setEmailViewDialogOpen(false);
    setEmailViewLoading(false);
    setEmailViewError(null);
    setEmailViewData(null);
    setEmailViewRow(null);
  }, [subjectKey]);

  useEffect(() => {
    return () => {
      if (recordingUrl) {
        try {
          window.URL.revokeObjectURL(recordingUrl);
        } catch (_) {}
      }
    };
  }, [recordingUrl]);

  const closeRecordingDialog = () => {
    setRecordingDialogOpen(false);
    setRecordingLoading(false);
    setRecordingError(null);
    setRecordingCall(null);
    if (recordingUrl) {
      try {
        window.URL.revokeObjectURL(recordingUrl);
      } catch (_) {}
    }
    setRecordingUrl(null);
  };

  const closePrivacyDialog = () => {
    setPrivacyDialogOpen(false);
    setPrivacyPendingCall(null);
  };

  const openPrivacyDialog = (call) => {
    setPrivacyPendingCall(call || null);
    setPrivacyDialogOpen(true);
  };

  const confirmPrivacyDialog = async () => {
    const call = privacyPendingCall;
    setPrivacyDialogOpen(false);
    setPrivacyPendingCall(null);
    if (call) {
      await openRecordingDialog(call);
    }
  };

  const closeEmailPrivacyDialog = () => {
    setEmailPrivacyDialogOpen(false);
    setEmailPrivacyPending(null);
  };

  const openEmailPrivacyDialog = (row) => {
    setEmailPrivacyPending(row || null);
    setEmailPrivacyDialogOpen(true);
  };

  const closeEmailViewDialog = () => {
    setEmailViewDialogOpen(false);
    setEmailViewLoading(false);
    setEmailViewError(null);
    setEmailViewData(null);
    setEmailViewRow(null);
  };

  const openEmailViewDialog = async (row) => {
    const accountId = row?.account_id;
    const uid = row?.uid;
    const folder = row?.folder;
    if (!accountId || !uid || !folder) return;

    setEmailViewRow(row);
    setEmailViewDialogOpen(true);
    setEmailViewLoading(true);
    setEmailViewError(null);
    setEmailViewData(null);

    try {
      const res = await api.get('/email-accounts/emails/body', {
        params: {
          account_id: Number(accountId),
          uid: String(uid),
          folder: String(folder)
        }
      });
      const data = res.data || null;
      const hasBody =
        Boolean(String(data?.html || '').trim()) || Boolean(String(data?.text || '').trim());
      if (hasBody) {
        setEmailViewData(data);
        return;
      }

      const fallback = await api.get(
        `/email-accounts/${encodeURIComponent(String(accountId))}/emails/${encodeURIComponent(String(uid))}/body`,
        { params: { folder: String(folder), mark_seen: 0 } }
      );
      setEmailViewData(fallback.data || null);
    } catch (e) {
      setEmailViewError(e?.response?.data?.message || 'Errore nel recupero del messaggio');
    } finally {
      setEmailViewLoading(false);
    }
  };

  const confirmEmailPrivacyDialog = async () => {
    const row = emailPrivacyPending;
    setEmailPrivacyDialogOpen(false);
    setEmailPrivacyPending(null);
    if (row) {
      await openEmailViewDialog(row);
    }
  };

  const openRecordingDialog = async (call) => {
    const callId = String(call?.call_id || '').trim();
    setRecordingCall(call || null);
    setRecordingDialogOpen(true);
    setRecordingLoading(true);
    setRecordingError(null);
    if (recordingUrl) {
      try {
        window.URL.revokeObjectURL(recordingUrl);
      } catch (_) {}
      setRecordingUrl(null);
    }

    if (!callId) {
      setRecordingLoading(false);
      setRecordingError('Registrazione non disponibile');
      return;
    }

    try {
      const response = await api.get(`/cloudtalk/recording/${encodeURIComponent(callId)}`, {
        responseType: 'blob',
      });
      const blob = new Blob([response.data], {
        type: response.headers?.['content-type'] || 'audio/x-wav',
      });
      const url = window.URL.createObjectURL(blob);
      setRecordingUrl(url);
    } catch (e) {
      const msg = await parseBlobErrorMessage(e?.response?.data);
      setRecordingError(msg || e?.response?.data?.message || 'Registrazione non disponibile');
    } finally {
      setRecordingLoading(false);
    }
  };

  useEffect(() => {
    if (tab !== 6) return;
    if (!telefono) return;

    let cancelled = false;
    const load = async () => {
      setCloudtalkLoading(true);
      try {
        const orderBy = 'started_at';
        const orderDirection = 'desc';

        if (!cloudtalkSearchTerm) {
          const candidates = getPhoneSearchCandidates(telefono);
          const fallback = candidates[0] || telefono;

          let picked = fallback;
          let pickedRows = [];
          let pickedTotal = 0;
          let foundMatch = false;

          for (const c of candidates.length ? candidates : [fallback]) {
            const res = await api.get('/cloudtalk', {
              params: {
                page: 1,
                limit: cloudtalkRowsPerPage,
                search: c,
                orderBy,
                orderDirection,
              },
            });
            const rows = Array.isArray(res.data?.data) ? res.data.data : [];
            const total = Number(res.data?.meta?.total || 0);
            if (rows.length || total) {
              picked = c;
              pickedRows = rows;
              pickedTotal = total;
              foundMatch = true;
              break;
            }
          }

          if (cancelled) return;
          if (!foundMatch) {
            picked = fallback;
          }
          setCloudtalkSearchTerm(picked);
          setCloudtalkCalls(pickedRows);
          setCloudtalkTotal(pickedTotal);
          setCloudtalkPage(0);
          cloudtalkLoadedRef.current = {
            phone: telefono,
            searchTerm: picked,
            page: 0,
            rowsPerPage: cloudtalkRowsPerPage,
          };
          return;
        }

        const alreadyLoaded =
          cloudtalkLoadedRef.current.phone === telefono &&
          cloudtalkLoadedRef.current.searchTerm === cloudtalkSearchTerm &&
          cloudtalkLoadedRef.current.page === cloudtalkPage &&
          cloudtalkLoadedRef.current.rowsPerPage === cloudtalkRowsPerPage;

        if (alreadyLoaded) return;

        const res = await api.get('/cloudtalk', {
          params: {
            page: cloudtalkPage + 1,
            limit: cloudtalkRowsPerPage,
            search: cloudtalkSearchTerm,
            orderBy,
            orderDirection,
          },
        });
        if (cancelled) return;
        setCloudtalkCalls(Array.isArray(res.data?.data) ? res.data.data : []);
        setCloudtalkTotal(Number(res.data?.meta?.total || 0));
        cloudtalkLoadedRef.current = {
          phone: telefono,
          searchTerm: cloudtalkSearchTerm,
          page: cloudtalkPage,
          rowsPerPage: cloudtalkRowsPerPage,
        };
      } catch (_e) {
        if (!cancelled) toast.error('Errore nel caricamento delle chiamate Cloudtalk');
      } finally {
        if (!cancelled) setCloudtalkLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [tab, telefono, cloudtalkPage, cloudtalkRowsPerPage, cloudtalkSearchTerm]);

  useEffect(() => {
    if (tab !== 7) return;
    if (!emailSearchCandidates.length) return;
    const term = emailSearchCandidates.join('|');
    setEmailSearchTerm(term);
    setEmailPage(0);
  }, [tab, emailSearchCandidates]);

  useEffect(() => {
    if (tab !== 7) return;
    if (!emailSearchTerm) return;

    let cancelled = false;
    const load = async () => {
      setEmailLoading(true);
      try {
        const res = await api.get('/email-accounts/emails/search', {
          params: {
            page: emailPage + 1,
            limit: emailRowsPerPage,
            search: emailSearchTerm,
            orderBy: 'date',
            order: 'desc'
          }
        });
        if (cancelled) return;
        const rows = Array.isArray(res.data?.emails) ? res.data.emails : [];
        setEmailMatches(rows);
        setEmailTotal(Number(res.data?.total || 0));
      } catch (_e) {
        if (!cancelled) toast.error('Errore nel caricamento Email/PEC');
      } finally {
        if (!cancelled) setEmailLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [tab, emailSearchTerm, emailPage, emailRowsPerPage]);

  const handleGenerateReport = async () => {
    if (!subjectKey || generatingReport) return;
    setGeneratingReport(true);
    try {
      const res = await api.post('/reports/passp-posizione-contabile', {
        subjectKey: String(subjectKey || ''),
      });
      const jobId = res.data?.jobId;
      if (!jobId) throw new Error('JobId mancante');
      addJob(jobId, 'passp_posizione_contabile_report');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Errore generazione report posizione');
    } finally {
      setGeneratingReport(false);
    }
  };
  const contrattiUpsellSorted = useMemo(
    () =>
      [...contrattiUpsell].sort((a, b) => {
        const aTime = a?.data_conferimento ? new Date(a.data_conferimento).getTime() : 0;
        const bTime = b?.data_conferimento ? new Date(b.data_conferimento).getTime() : 0;
        if (aTime !== bTime) return aTime - bTime;
        return Number(a?.id || 0) - Number(b?.id || 0);
      }),
    [contrattiUpsell]
  );
  const totalContratti = useMemo(
    () =>
      contrattiUpsell.reduce((sum, row) => {
        const isUpsell = String(row?.tipo_contratto || '').toLowerCase() === 'upsell';
        if (isUpsell) return sum;
        return sum + Number(row?.importo_totale || 0);
      }, 0),
    [contrattiUpsell]
  );
  const totalUpsell = useMemo(
    () =>
      contrattiUpsell.reduce((sum, row) => {
        const isUpsell = String(row?.tipo_contratto || '').toLowerCase() === 'upsell';
        if (!isUpsell) return sum;
        return sum + Number(row?.importo_totale || 0);
      }, 0),
    [contrattiUpsell]
  );
  const totalTransazioni = useMemo(() => Number(totali?.transazioni_importo || 0), [totali]);
  const totalFatturato = useMemo(
    () => fatturato.reduce((sum, row) => sum + Number(row?.totale || 0), 0),
    [fatturato]
  );
  const totaleContrattiUpsell = useMemo(
    () => Number(totalContratti || 0) + Number(totalUpsell || 0),
    [totalContratti, totalUpsell]
  );
  const saldoContabile = useMemo(
    () => totaleContrattiUpsell - totalFatturato,
    [totaleContrattiUpsell, totalFatturato]
  );
  const percentualeIncassato = useMemo(() => {
    if (!totaleContrattiUpsell) return 0;
    return (totalFatturato / totaleContrattiUpsell) * 100;
  }, [totaleContrattiUpsell, totalFatturato]);

  const getNumberColor = (value) => (Number(value || 0) > 0 ? 'success.main' : 'error.main');
  const fattureDetails = useMemo(() => {
    const all = [];
    fatturato.forEach((y) => {
      (y?.dettaglio_documenti || []).forEach((d) => {
        if (String(d?.tipo || '').toUpperCase() === 'FT' && d?.data_documento) all.push(d);
      });
    });
    return all;
  }, [fatturato]);
  const fattureDatesDiff = useMemo(() => {
    const dates = fattureDetails
      .map((d) => new Date(d.data_documento))
      .filter((d) => !Number.isNaN(d.getTime()))
      .sort((a, b) => a.getTime() - b.getTime());
    const diffs = [];
    for (let i = 1; i < dates.length; i += 1) {
      const diff = Math.round((dates[i].getTime() - dates[i - 1].getTime()) / (1000 * 60 * 60 * 24));
      if (diff >= 0) diffs.push(diff);
    }
    return diffs;
  }, [fattureDetails]);
  const frequenzaPagamenti = useMemo(() => {
    if (!fattureDatesDiff.length) return { periodicita: '-', media: 0, dev: 0, testo: '-' };
    const media = fattureDatesDiff.reduce((s, v) => s + v, 0) / fattureDatesDiff.length;
    const variance =
      fattureDatesDiff.reduce((s, v) => s + (v - media) * (v - media), 0) / fattureDatesDiff.length;
    const dev = Math.sqrt(variance);
    let periodicita = 'Annuale';
    if (media <= 10) periodicita = 'Settimanale';
    else if (media <= 20) periodicita = 'Quindicinale';
    else if (media <= 40) periodicita = 'Mensile';
    else if (media <= 100) periodicita = 'Trimestrale';
    return {
      periodicita,
      media,
      dev,
      testo: `${periodicita} (media: ${formatDecimal(media)}, dev: ${formatDecimal(dev)} giorni)`,
    };
  }, [fattureDatesDiff]);

  const renderContrattiUpsellTable = (rows) => (
    <Card sx={sectionCardSx}>
      {renderSectionHeader('Contratti / Upsell')}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Resp. Comm.</TableCell>
              <TableCell>Resp. Contabile</TableCell>
              <TableCell>Resp. Upsell</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Importo Totale</TableCell>
              <TableCell>Acconto</TableCell>
              <TableCell>Data Conferimento</TableCell>
              <TableCell>Stato</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell>{row.responsabile_commerciale || '-'}</TableCell>
                <TableCell>{row.responsabile_contabile || '-'}</TableCell>
                <TableCell>{row.responsabile_upsell || '-'}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={getContractTypeLabel(row.tipo_contratto)}
                    color={String(row.tipo_contratto || '').toLowerCase() === 'upsell' ? 'warning' : 'info'}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 800, color: 'success.main' }}>
                    {formatCurrency(row.importo_totale)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {getContractTypeLabel(row.tipo_contratto)}
                    {Number(row.number_installment || 0) > 0
                      ? ` • ${Number(row.number_installment)} rate`
                      : ''}
                  </Typography>
                </TableCell>
                <TableCell>{formatCurrency(row.acconto)}</TableCell>
                <TableCell>{formatDate(row.data_conferimento)}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={getDossierStatusLabel(row.status)}
                    sx={{ borderRadius: 99, fontWeight: 700 }}
                  />
                </TableCell>
              </TableRow>
            ))}
            {!rows.length ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  Nessun dato disponibile
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );

  const renderPianiRateazioneTable = (rows) => (
    <Card sx={sectionCardSx}>
      {renderSectionHeader('Piani Rateazione')}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Tipo</TableCell>
              <TableCell>Azienda</TableCell>
              <TableCell>Stato</TableCell>
              <TableCell>Importo piano</TableCell>
              <TableCell>Incassato</TableCell>
              <TableCell>Saldo</TableCell>
              <TableCell>Avanzamento</TableCell>
              <TableCell>Data decorrenza piano</TableCell>
              <TableCell>Referente</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              const importoPiano = Number(row.importo_totale || 0);
              const incassato = Number(row.acconto || 0);
              const saldo = Math.max(0, importoPiano - incassato);
              const progress = getProgressPercent(incassato, importoPiano);
              return (
                <TableRow key={`piano-${row.id}`}>
                  <TableCell>
                    <Chip size="small" label={getContractTypeLabel(row.tipo_contratto)} color="info" variant="outlined" />
                  </TableCell>
                  <TableCell>{row.azienda || '-'}</TableCell>
                  <TableCell>
                    <Chip size="small" label={getDossierStatusLabel(row.status)} color={String(row.status || '').toLowerCase() === 'active' ? 'success' : 'default'} />
                  </TableCell>
                  <TableCell>{formatCurrency(importoPiano)}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={formatCurrency(incassato)}
                      sx={{ bgcolor: 'rgba(22, 163, 74, 0.1)', color: 'success.main', fontWeight: 700 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={formatCurrency(saldo)}
                      sx={{
                        bgcolor: saldo <= 0 ? 'rgba(22, 163, 74, 0.1)' : 'rgba(245, 158, 11, 0.12)',
                        color: saldo <= 0 ? 'success.main' : 'warning.dark',
                        fontWeight: 700,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ position: 'relative', width: 52, height: 52 }}>
                      <CircularProgress
                        variant="determinate"
                        value={100}
                        size={52}
                        thickness={5}
                        sx={{ color: '#e7edf3', position: 'absolute', left: 0 }}
                      />
                      <CircularProgress
                        variant="determinate"
                        value={progress}
                        size={52}
                        thickness={5}
                        sx={{ color: '#f7b500', position: 'absolute', left: 0 }}
                      />
                      <Box
                        sx={{
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          position: 'absolute',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Typography variant="caption" sx={{ fontWeight: 700 }}>
                          {progress.toFixed(2)}%
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>{formatDate(row.data_conferimento)}</TableCell>
                  <TableCell>{row.responsabile_upsell || row.responsabile_commerciale || row.responsabile_contabile || '-'}</TableCell>
                </TableRow>
              );
            })}
            {!rows.length ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  Nessun dato disponibile
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: '#eef2ff',
              color: '#4f46e5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <WalletCards size={20} />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800 }}>
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Esposizione Contabile • Dettaglio nominativo
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            variant="contained"
            startIcon={generatingReport ? <CircularProgress size={16} /> : <FileText size={16} />}
            onClick={handleGenerateReport}
            disabled={loading || generatingReport}
            sx={{ textTransform: 'none' }}
          >
            Report Posizione
          </Button>
          <Button
            variant="outlined"
            startIcon={<ArrowLeft size={16} />}
            onClick={() => navigate('/passp-esposizione-contabile')}
          >
            Torna elenco
          </Button>
        </Box>
      </Box>

      <Tabs
        value={tab}
        onChange={(_e, val) => setTab(val)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{ px: 0 }}
      >
        <Tab label="Dati anagrafici" />
        <Tab label="Sintesi Posizione" />
        <Tab label="Contratti/Upsell" />
        <Tab label="Piani Rateazione" />
        <Tab label="Fatturato" />
        <Tab label="Aziende collegate" />
        <Tab label="Cloudtalk" />
        <Tab label="Email/PEC" />
      </Tabs>

      <Box sx={{ pt: 1.6 }}>
        {loading ? <LoaderProgress /> : null}

        {!loading && tab === 0 ? (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Card sx={sectionCardSx}>
                <Box sx={{ p: 3, borderBottom: '1px solid rgba(145, 158, 171, 0.12)' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IdCard size={18} color="#94a3b8" />
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Dati anagrafici
                    </Typography>
                  </Box>
                </Box>
                  <Box sx={{ p: 3, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '140px 1fr 140px 1fr' }, rowGap: 0.75 }}>
                    <Typography variant="body2" color="text.secondary">Cliente</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{anagrafica.nome || '-'}</Typography>
                    <Typography variant="body2" color="text.secondary">Codice fiscale</Typography>
                    <Typography variant="body2">{anagrafica.codice_fiscale || '-'}</Typography>
                    <Typography variant="body2" color="text.secondary">Partita IVA</Typography>
                    <Typography variant="body2">{anagrafica.partita_iva || '-'}</Typography>
                    <Typography variant="body2" color="text.secondary">Data di nascita</Typography>
                    <Typography variant="body2">{birthDateValue || '-'}</Typography>
                  </Box>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card sx={sectionCardSx}>
                <Box sx={{ p: 3, borderBottom: '1px solid rgba(145, 158, 171, 0.12)' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Mail size={18} color="#94a3b8" />
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Contatti
                    </Typography>
                  </Box>
                </Box>
                  <Box sx={{ p: 3, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '140px 1fr 140px 1fr' }, rowGap: 0.75 }}>
                    <Typography variant="body2" color="text.secondary">Email</Typography>
                    <Typography variant="body2">{anagrafica.email || '-'}</Typography>
                    <Typography variant="body2" color="text.secondary">Telefono</Typography>
                    <Typography variant="body2">{anagrafica.telefono || '-'}</Typography>
                  </Box>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card sx={sectionCardSx}>
                <Box sx={{ p: 3, borderBottom: '1px solid rgba(145, 158, 171, 0.12)' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <MapPin size={18} color="#94a3b8" />
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Indirizzo
                    </Typography>
                  </Box>
                </Box>
                  <Box sx={{ p: 3, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '140px 1fr 140px 1fr' }, rowGap: 0.75 }}>
                    <Typography variant="body2" color="text.secondary">Indirizzo</Typography>
                    <Typography variant="body2">{anagrafica.indirizzo || '-'}</Typography>
                    <Typography variant="body2" color="text.secondary">Città</Typography>
                    <Typography variant="body2">{anagrafica.localita || '-'}</Typography>
                    <Typography variant="body2" color="text.secondary">Provincia / CAP</Typography>
                    <Typography variant="body2">
                      {[anagrafica.provincia, anagrafica.cap].filter(Boolean).join(' / ') || '-'}
                    </Typography>
                  </Box>
              </Card>
            </Grid>
          </Grid>
        ) : null}

        {!loading && tab === 1 ? (
          <Card sx={sectionCardSx}>
            {renderSectionHeader('Sintesi Posizione')}
            <Box sx={{ p: 3 }}>
              <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', pb: 2, mb: 2.2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Riepilogo Finanziario
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Panoramica finanziaria del cliente
                </Typography>
                <Grid container spacing={2} sx={{ mt: 0.4 }}>
                  <Grid item xs={12} md={3}>
                    <Typography variant="body1" sx={{ fontWeight: 700 }}>
                      Totale Contratti
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.8 }}>
                      <FileText size={16} color="#9ca3af" />
                      <Typography variant="h6" sx={{ fontWeight: 800, color: getNumberColor(totalContratti) }}>
                        {formatCurrency(totalContratti)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="body1" sx={{ fontWeight: 700 }}>
                      Totale Upsell
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.8 }}>
                      <TrendingUp size={16} color="#9ca3af" />
                      <Typography variant="h6" sx={{ fontWeight: 800, color: getNumberColor(totalUpsell) }}>
                        {formatCurrency(totalUpsell)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="body1" sx={{ fontWeight: 700 }}>
                      Totale Transazioni
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.8 }}>
                      <RefreshCw size={16} color="#9ca3af" />
                      <Typography variant="h6" sx={{ fontWeight: 800, color: getNumberColor(totalTransazioni) }}>
                        {formatCurrency(totalTransazioni)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="body1" sx={{ fontWeight: 700 }}>
                      Totale Fatturato
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.8 }}>
                      <Receipt size={16} color="#9ca3af" />
                      <Typography variant="h6" sx={{ fontWeight: 800, color: getNumberColor(totalFatturato) }}>
                        {formatCurrency(totalFatturato)}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Situazione Clienti
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Stato dei pagamenti
                </Typography>
                <Grid container spacing={2} sx={{ mt: 0.4 }}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body1" sx={{ fontWeight: 700 }}>
                      Saldo
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.8 }}>
                      <Scale size={16} color="#9ca3af" />
                      <Typography variant="h6" sx={{ fontWeight: 800, color: getNumberColor(saldoContabile) }}>
                        {formatCurrency(saldoContabile)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body1" sx={{ fontWeight: 700 }}>
                      Percentuale Incassato
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.8 }}>
                      <PieChart size={16} color="#9ca3af" />
                      <Typography variant="h6" sx={{ fontWeight: 800, color: getNumberColor(percentualeIncassato) }}>
                        {formatPercent(percentualeIncassato)}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </Card>
        ) : null}

        {!loading && tab === 2 ? (
          renderContrattiUpsellTable(contrattiUpsellSorted)
        ) : null}

        {!loading && tab === 3 ? renderPianiRateazioneTable(contrattiUpsellSorted) : null}

        {!loading && tab === 4 ? (
            <Card sx={sectionCardSx}>
              {renderSectionHeader('Fatturato')}
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Anno</TableCell>
                      <TableCell align="right">Documenti</TableCell>
                      <TableCell align="right">Totale</TableCell>
                      <TableCell align="right">Contratti/Upsell</TableCell>
                      <TableCell align="right">Transazioni</TableCell>
                      <TableCell>Riferimenti FT/NC</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {fatturato.map((row) => (
                      <React.Fragment key={row.anno}>
                        <TableRow hover>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                              <IconButton
                                size="small"
                                onClick={() => setExpandedAnno(expandedAnno === row.anno ? null : row.anno)}
                              >
                                {expandedAnno === row.anno ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                              </IconButton>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 700, cursor: 'pointer' }}
                                onClick={() => setExpandedAnno(expandedAnno === row.anno ? null : row.anno)}
                              >
                                {row.anno}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right">{row.documenti}</TableCell>
                          <TableCell align="right">{formatCurrency(row.totale)}</TableCell>
                          <TableCell align="right">{formatCurrency(row.totale_contratti_upsell)}</TableCell>
                          <TableCell align="right">{formatCurrency(row.totale_transazioni)}</TableCell>
                          <TableCell>
                            <Typography variant="caption" sx={{ display: 'block', fontWeight: 700 }}>
                              FT: {Array.isArray(row.riferimenti_ft) && row.riferimenti_ft.length ? row.riferimenti_ft.join(', ') : '-'}
                            </Typography>
                            <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, mt: 0.4 }}>
                              NC: {Array.isArray(row.riferimenti_nc) && row.riferimenti_nc.length ? row.riferimenti_nc.join(', ') : '-'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={6} sx={{ py: 0, borderBottom: 0 }}>
                            <Collapse in={expandedAnno === row.anno} timeout="auto" unmountOnExit>
                              <Box sx={{ py: 1.2, px: 1.5 }}>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Tipo</TableCell>
                                      <TableCell>Riferimento</TableCell>
                                      <TableCell>Data</TableCell>
                                      <TableCell>Azienda</TableCell>
                                      <TableCell align="right">Importo</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {(row.dettaglio_documenti || []).map((doc) => (
                                      <TableRow key={`${row.anno}-${doc.id}`}>
                                        <TableCell>{doc.tipo || '-'}</TableCell>
                                        <TableCell>{doc.riferimento || '-'}</TableCell>
                                        <TableCell>{formatDate(doc.data_documento)}</TableCell>
                                        <TableCell>{doc.azienda || '-'}</TableCell>
                                        <TableCell align="right">{formatCurrency(doc.importo)}</TableCell>
                                      </TableRow>
                                    ))}
                                    {!(row.dettaglio_documenti || []).length ? (
                                      <TableRow>
                                        <TableCell colSpan={5} align="center">
                                          Nessun dettaglio disponibile
                                        </TableCell>
                                      </TableRow>
                                    ) : null}
                                  </TableBody>
                                </Table>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))}
                    {!fatturato.length ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          Nessun dato disponibile
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
        ) : null}

        {!loading && tab === 5 ? (
          <Card sx={sectionCardSx}>
            {renderSectionHeader('Aziende collegate')}
            <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                Informazioni Finanziarie
              </Typography>
              <Grid container spacing={1.2}>
                <Grid item xs={12} md={3}>
                  <Typography variant="body1" color="text.secondary">Totale Fatturato:</Typography>
                </Grid>
                <Grid item xs={12} md={9}>
                  <Typography variant="body1" sx={{ fontWeight: 800, color: getNumberColor(totalFatturato) }}>
                    {formatCurrency(totalFatturato)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="body1" color="text.secondary">Periodicità:</Typography>
                </Grid>
                <Grid item xs={12} md={9}>
                  <Typography variant="body1" sx={{ fontWeight: 700 }}>{frequenzaPagamenti.periodicita}</Typography>
                </Grid>
              </Grid>
            </Box>
            <Box sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                Altri Dettagli
              </Typography>
              <Grid container spacing={1.2}>
                <Grid item xs={12} md={3}>
                  <Typography variant="body1" color="text.secondary">Nr. Fatture emesse:</Typography>
                </Grid>
                <Grid item xs={12} md={9}>
                  <Typography variant="body1" sx={{ fontWeight: 800, color: getNumberColor(fattureDetails.length) }}>
                    {fattureDetails.length}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="body1" color="text.secondary">Frequenza Pagamenti:</Typography>
                </Grid>
                <Grid item xs={12} md={9}>
                  <Typography variant="body1">{frequenzaPagamenti.testo}</Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="body1" color="text.secondary">Differenze Date Fatture:</Typography>
                </Grid>
                <Grid item xs={12} md={9}>
                  <Typography variant="body1">
                    {fattureDatesDiff.length
                      ? fattureDatesDiff.map((d) => `${d} giorni`).join(', ')
                      : '-'}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Azienda</TableCell>
                    <TableCell>Anno</TableCell>
                    <TableCell>Codice cliente</TableCell>
                    <TableCell>Nome azienda</TableCell>
                    <TableCell>CF/P.IVA azienda</TableCell>
                    <TableCell>Stato</TableCell>
                    <TableCell>Tipo</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dettagli.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>
                        <Chip size="small" label={row.azienda || '-'} />
                      </TableCell>
                      <TableCell>{row.anno || '-'}</TableCell>
                      <TableCell>{row.codice || '-'}</TableCell>
                      <TableCell>{row.azienda_ragione_sociale || '-'}</TableCell>
                      <TableCell>{row.azienda_cod_fis_piva || '-'}</TableCell>
                      <TableCell>{row.azienda_stato || '-'}</TableCell>
                      <TableCell>{row.azienda_tipo || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        ) : null}

        {!loading && tab === 6 ? (
          <Card sx={sectionCardSx}>
            {renderSectionHeader('Cloudtalk')}
            <Box sx={{ p: 2.2 }}>
              {!telefono ? (
                <Typography variant="body2" color="text.secondary">
                  Telefono non disponibile: impossibile incrociare le chiamate Cloudtalk.
                </Typography>
              ) : (
                <>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 1.5 }}>
                    <Chip size="small" icon={<PhoneCall size={14} />} label={`Telefono: ${telefono}`} />
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`Filtro: ${cloudtalkSearchTerm || '…'}`}
                    />
                    <Chip
                      size="small"
                      color="primary"
                      variant="outlined"
                      label={`Totale: ${cloudtalkTotal}`}
                    />
                  </Box>

                  <TableContainer>
                    <Table size="small" sx={{ '& .MuiTableCell-root': { fontSize: '0.875rem' } }}>
                      <TableHead>
                        <TableRow>
                          <TableCell>Data/Ora</TableCell>
                          <TableCell>Direzione</TableCell>
                          <TableCell>Da</TableCell>
                          <TableCell>A</TableCell>
                          <TableCell>Stato</TableCell>
                          <TableCell>Durata</TableCell>
                          <TableCell>Agente</TableCell>
                          <TableCell align="right">Azioni</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {cloudtalkLoading ? (
                          <TableRow>
                            <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                              <CircularProgress size={22} />
                            </TableCell>
                          </TableRow>
                        ) : cloudtalkCalls.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                              Nessuna chiamata trovata
                            </TableCell>
                          </TableRow>
                        ) : (
                          cloudtalkCalls.map((call) => {
                            const direction = String(call?.direction || '');
                            const dirLabel = translateCloudtalkDirection(direction);
                            const statusLabel = translateCloudtalkStatus(call?.status);
                            const statusLower = String(call?.status || '').toLowerCase();
                            const statusColor =
                              statusLower === 'answered'
                                ? 'success'
                                : statusLower === 'missed'
                                  ? 'error'
                                  : 'default';
                            return (
                              <TableRow key={call?.id ?? call?.call_id} hover>
                                <TableCell>{formatDateTimeIt(call?.started_at)}</TableCell>
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                                    {direction === 'inbound' ? (
                                      <PhoneIncoming size={16} color="#16a34a" />
                                    ) : direction === 'outbound' ? (
                                      <PhoneOutgoing size={16} color="#2563eb" />
                                    ) : (
                                      <PhoneCall size={16} />
                                    )}
                                    <Typography variant="body2">{dirLabel}</Typography>
                                  </Box>
                                </TableCell>
                                <TableCell>{call?.from_number || '-'}</TableCell>
                                <TableCell>{call?.to_number || '-'}</TableCell>
                                <TableCell>
                                  <Chip size="small" color={statusColor} label={statusLabel} />
                                </TableCell>
                                <TableCell>{formatDuration(call?.duration)}</TableCell>
                                <TableCell>{call?.agent_name || '-'}</TableCell>
                                <TableCell align="right">
                                  <Tooltip title="Ascolta registrazione">
                                    <span>
                                      <IconButton
                                        size="small"
                                        onClick={() => openPrivacyDialog(call)}
                                        disabled={!call?.call_id}
                                      >
                                        <Headphones size={18} />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <TablePagination
                    component="div"
                    count={cloudtalkTotal}
                    page={cloudtalkPage}
                    rowsPerPage={cloudtalkRowsPerPage}
                    onPageChange={(_e, newPage) => setCloudtalkPage(newPage)}
                    onRowsPerPageChange={(e) => {
                      setCloudtalkRowsPerPage(parseInt(e.target.value, 10));
                      setCloudtalkPage(0);
                      cloudtalkLoadedRef.current = { phone: null, searchTerm: null, page: null, rowsPerPage: null };
                    }}
                    rowsPerPageOptions={[10, 25, 50]}
                    labelRowsPerPage="Righe per pagina:"
                  />

                  <Dialog open={recordingDialogOpen} onClose={closeRecordingDialog} maxWidth="sm" fullWidth>
                    <DialogTitle>
                      Registrazione chiamata
                    </DialogTitle>
                    <DialogContent>
                      <Box sx={{ mt: 1 }}>
                        {recordingLoading ? (
                          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                            <CircularProgress size={22} />
                          </Box>
                        ) : recordingError ? (
                          <Typography variant="body2" color="error">
                            {recordingError}
                          </Typography>
                        ) : recordingUrl ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            <audio controls src={recordingUrl} style={{ width: '100%' }} />
                            <Button
                              variant="outlined"
                              component="a"
                              href={recordingUrl}
                              download={
                                recordingCall?.call_id
                                  ? `cloudtalk_recording_${String(recordingCall.call_id)}.wav`
                                  : 'cloudtalk_recording.wav'
                              }
                              startIcon={<Download size={16} />}
                              sx={{ textTransform: 'none', alignSelf: 'flex-start' }}
                            >
                              Scarica audio
                            </Button>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Registrazione non disponibile
                          </Typography>
                        )}
                      </Box>
                    </DialogContent>
                    <DialogActions>
                      <Button onClick={closeRecordingDialog} sx={{ textTransform: 'none' }}>
                        Chiudi
                      </Button>
                    </DialogActions>
                  </Dialog>

                  <Dialog open={privacyDialogOpen} onClose={closePrivacyDialog} maxWidth="sm" fullWidth>
                    <DialogTitle>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AlertTriangle size={18} color="#ef4444" />
                        <span>Conferma Privacy</span>
                      </Box>
                    </DialogTitle>
                    <DialogContent>
                      <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Typography>
                          Stai per ascoltare una registrazione telefonica. Assicurati di avere un motivo legittimo e
                          l’autorizzazione necessaria, in conformità alla normativa privacy e alle policy aziendali.
                        </Typography>
                        {privacyPendingCall ? (
                          <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Direzione</TableCell>
                                  <TableCell>Da</TableCell>
                                  <TableCell>A</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                <TableRow>
                                  <TableCell sx={{ verticalAlign: 'top' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      {String(privacyPendingCall?.direction || '') === 'inbound' ? (
                                        <PhoneIncoming size={16} color="#16a34a" />
                                      ) : String(privacyPendingCall?.direction || '') === 'outbound' ? (
                                        <PhoneOutgoing size={16} color="#2563eb" />
                                      ) : (
                                        <PhoneCall size={16} />
                                      )}
                                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {translateCloudtalkDirection(privacyPendingCall?.direction)}
                                      </Typography>
                                    </Box>
                                  </TableCell>
                                  <TableCell sx={{ verticalAlign: 'top' }}>
                                    {(() => {
                                      const { daName } = getPrivacyNames(privacyPendingCall);
                                      return (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1 }}>
                                          <Typography variant="body2">
                                            {formatPhone(privacyPendingCall?.from_number)}
                                          </Typography>
                                          {daName ? (
                                            <Typography variant="caption" color="text.secondary">{`(${daName})`}</Typography>
                                          ) : null}
                                        </Box>
                                      );
                                    })()}
                                  </TableCell>
                                  <TableCell sx={{ verticalAlign: 'top' }}>
                                    {(() => {
                                      const { aName } = getPrivacyNames(privacyPendingCall);
                                      return (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1 }}>
                                          <Typography variant="body2">
                                            {formatPhone(privacyPendingCall?.to_number)}
                                          </Typography>
                                          {aName ? (
                                            <Typography variant="caption" color="text.secondary">{`(${aName})`}</Typography>
                                          ) : null}
                                        </Box>
                                      );
                                    })()}
                                  </TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </TableContainer>
                        ) : null}
                      </Box>
                    </DialogContent>
                    <DialogActions>
                      <Button onClick={closePrivacyDialog} sx={{ textTransform: 'none' }}>
                        Annulla
                      </Button>
                      <Button onClick={confirmPrivacyDialog} variant="contained" autoFocus sx={{ textTransform: 'none' }}>
                        Conferma
                      </Button>
                    </DialogActions>
                  </Dialog>
                </>
              )}
            </Box>
          </Card>
        ) : null}

        {!loading && tab === 7 ? (
          <Card sx={sectionCardSx}>
            {renderSectionHeader('Email/PEC')}
            <Box sx={{ p: 2.2 }}>
              {!emailSearchCandidates.length ? (
                <Typography variant="body2" color="text.secondary">
                  Email/PEC non disponibile: impossibile incrociare i messaggi.
                </Typography>
              ) : (
                <>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 1.5 }}>
                    <Chip size="small" icon={<Mail size={14} />} label={`Filtro: ${emailSearchTerm || emailSearchCandidates[0]}`} />
                    <Chip size="small" color="primary" variant="outlined" label={`Totale: ${emailTotal}`} />
                  </Box>

                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Data/Ora</TableCell>
                          <TableCell>Direzione</TableCell>
                          <TableCell>Account</TableCell>
                          <TableCell>Da</TableCell>
                          <TableCell>A</TableCell>
                          <TableCell>Oggetto</TableCell>
                          <TableCell align="center">Allegati</TableCell>
                          <TableCell align="right">Azioni</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {emailLoading ? (
                          <TableRow>
                            <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                              <CircularProgress size={22} />
                            </TableCell>
                          </TableRow>
                        ) : emailMatches.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                              Nessun messaggio trovato
                            </TableCell>
                          </TableRow>
                        ) : (
                          emailMatches.map((row) => {
                            const outbound = isSentFolderPath(row.folder);
                            const folderLabel = translateFolderPathIt(row.folder);
                            return (
                              <TableRow key={`${row.account_id}-${row.folder}-${row.uid}`} hover>
                                  <TableCell>{formatDateTimeIt(row.date)}</TableCell>
                                  <TableCell>
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.35 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                      {outbound ? (
                                        <Send size={16} color="#2563eb" />
                                      ) : (
                                        <Inbox size={16} color="#16a34a" />
                                      )}
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontWeight: 600,
                                          color: outbound ? '#1d4ed8' : '#15803d',
                                        }}
                                      >
                                        {outbound ? 'Uscita' : 'Entrata'}
                                      </Typography>
                                    </Box>
                                    <Tooltip title={String(row.folder || '')} disableHoverListener={!row.folder}>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ lineHeight: 1.2, wordBreak: 'break-word' }}
                                      >
                                        {folderLabel}
                                      </Typography>
                                    </Tooltip>
                                  </Box>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                      {row.account_name || row.account_email || '-'}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {row.account_type || '-'}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>{row.from || '-'}</TableCell>
                                  <TableCell>{row.to || '-'}</TableCell>
                                  <TableCell>
                                    <Tooltip title={row.subject || ''} disableHoverListener={!row.subject}>
                                      <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                                        {row.subject || '-'}
                                      </Typography>
                                    </Tooltip>
                                  </TableCell>
                                <TableCell align="center">
                                  {row.has_attachments ? (
                                    <Paperclip size={16} color="#f59e0b" />
                                  ) : (
                                    <Typography variant="body2" color="text.secondary">
                                      -
                                    </Typography>
                                  )}
                                </TableCell>
                                  <TableCell align="right">
                                    <Tooltip title="Apri messaggio">
                                      <IconButton size="small" onClick={() => openEmailPrivacyDialog(row)}>
                                        <Eye size={18} />
                                      </IconButton>
                                    </Tooltip>
                                  </TableCell>
                                </TableRow>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <TablePagination
                    component="div"
                    count={emailTotal}
                    page={emailPage}
                    rowsPerPage={emailRowsPerPage}
                    onPageChange={(_e, newPage) => setEmailPage(newPage)}
                    onRowsPerPageChange={(e) => {
                      setEmailRowsPerPage(parseInt(e.target.value, 10));
                      setEmailPage(0);
                    }}
                    rowsPerPageOptions={[10, 25, 50]}
                    labelRowsPerPage="Righe per pagina:"
                  />

                  <Dialog open={emailPrivacyDialogOpen} onClose={closeEmailPrivacyDialog} maxWidth="sm" fullWidth>
                    <DialogTitle>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AlertTriangle size={18} color="#ef4444" />
                        <span>Conferma Privacy</span>
                      </Box>
                    </DialogTitle>
                    <DialogContent>
                      <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Typography>
                          Stai per visualizzare un messaggio Email/PEC. Assicurati di avere un motivo legittimo e
                          l’autorizzazione necessaria, in conformità alla normativa privacy e alle policy aziendali.
                        </Typography>
                        {emailPrivacyPending ? (
                          <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Account</TableCell>
                                  <TableCell>Cartella</TableCell>
                                  <TableCell>Data</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                <TableRow>
                                  <TableCell sx={{ verticalAlign: 'top' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                      {emailPrivacyPending.account_name || emailPrivacyPending.account_email || '-'}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {emailPrivacyPending.account_email || '-'}
                                    </Typography>
                                  </TableCell>
                                  <TableCell sx={{ verticalAlign: 'top' }}>
                                    <Tooltip
                                      title={String(emailPrivacyPending.folder || '')}
                                      disableHoverListener={!emailPrivacyPending.folder}
                                    >
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                        {isSentFolderPath(emailPrivacyPending.folder) ? (
                                          <Send size={16} color="#2563eb" />
                                        ) : (
                                          <Inbox size={16} color="#16a34a" />
                                        )}
                                        <Typography variant="body2">
                                          {translateFolderPathIt(emailPrivacyPending.folder)}
                                        </Typography>
                                      </Box>
                                    </Tooltip>
                                  </TableCell>
                                  <TableCell sx={{ verticalAlign: 'top' }}>
                                    <Typography variant="body2">{formatDateTimeIt(emailPrivacyPending.date)}</Typography>
                                  </TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </TableContainer>
                        ) : null}
                      </Box>
                    </DialogContent>
                    <DialogActions>
                      <Button onClick={closeEmailPrivacyDialog} sx={{ textTransform: 'none' }}>
                        Annulla
                      </Button>
                      <Button onClick={confirmEmailPrivacyDialog} variant="contained" autoFocus sx={{ textTransform: 'none' }}>
                        Conferma
                      </Button>
                    </DialogActions>
                  </Dialog>

                  <Dialog open={emailViewDialogOpen} onClose={closeEmailViewDialog} maxWidth="md" fullWidth>
                    <DialogTitle>
                      Messaggio Email/PEC
                    </DialogTitle>
                    <DialogContent>
                      <Box sx={{ mt: 1 }}>
                        {emailViewLoading ? (
                          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                            <CircularProgress size={22} />
                          </Box>
                        ) : emailViewError ? (
                          <Typography variant="body2" color="error">
                            {emailViewError}
                          </Typography>
                        ) : (
                          <>
                            <Paper
                              variant="outlined"
                              sx={{
                                p: 2,
                                borderRadius: 2,
                                bgcolor: 'background.paper',
                                mb: 2,
                              }}
                            >
                              <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                                {emailViewData?.subject || emailViewRow?.subject || '-'}
                              </Typography>
                              <Divider sx={{ my: 1.5 }} />
                              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '140px 1fr' }, rowGap: 0.9, columnGap: 1.2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                                  <User size={16} color="#64748b" />
                                  <Typography variant="body2" color="text.secondary">Da</Typography>
                                </Box>
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                  {emailViewData?.from || emailViewRow?.from || '-'}
                                </Typography>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                                  <Mail size={16} color="#64748b" />
                                  <Typography variant="body2" color="text.secondary">A</Typography>
                                </Box>
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                  {emailViewData?.to || emailViewRow?.to || '-'}
                                </Typography>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                                  <Calendar size={16} color="#64748b" />
                                  <Typography variant="body2" color="text.secondary">Data</Typography>
                                </Box>
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                  {formatDateTimeIt(emailViewData?.date || emailViewRow?.date)}
                                </Typography>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                                  <Folder size={16} color="#64748b" />
                                  <Typography variant="body2" color="text.secondary">Account</Typography>
                                </Box>
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                  {emailViewRow?.account_name || emailViewRow?.account_email || '-'}
                                  {' • '}
                                  {translateFolderPathIt(emailViewRow?.folder)}
                                </Typography>
                              </Box>
                            </Paper>

                            <Paper
                              variant="outlined"
                              sx={{
                                p: 2,
                                borderRadius: 2,
                                bgcolor: 'background.default',
                                borderLeft: '4px solid',
                                borderLeftColor: 'primary.main',
                              }}
                            >
                              {emailViewData?.html ? (
                                <Box
                                  className="email-body"
                                  sx={{
                                    fontFamily: 'inherit',
                                    fontSize: '0.95rem',
                                    lineHeight: 1.6,
                                    '& img': { maxWidth: '100%', height: 'auto' },
                                  }}
                                >
                                  <div dangerouslySetInnerHTML={{ __html: emailViewData.html }} />
                                </Box>
                              ) : (
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                  {emailViewData?.text || '(Nessun contenuto)'}
                                </Typography>
                              )}
                            </Paper>
                          </>
                        )}
                      </Box>
                    </DialogContent>
                    <DialogActions>
                      <Button onClick={closeEmailViewDialog} sx={{ textTransform: 'none' }}>
                        Chiudi
                      </Button>
                    </DialogActions>
                  </Dialog>
                </>
              )}
            </Box>
          </Card>
        ) : null}
      </Box>
    </Box>
  );
}
